<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  runtimeAnalyticsEventsForVisit,
  runtimeBadgeForVisit,
  runtimeEventDigest,
  runtimeEventsForVisit,
  runtimeMatrixForVisit,
  runtimeStatusLabel,
  runtimeToneClass,
} from '../utils/visitOperationalRuntime';

const props = defineProps({
  visit: { type: Object, default: () => ({}) },
  maxEvents: { type: Number, default: 5 },
});

const TEXT = Object.freeze({
  title: 'Runtime operacional',
  unknownJob: 'Job não informado',
  eventsTitle: 'Eventos, filas e tentativas',
  analyticsTitle: 'Analytics runtime',
  noQueue: 'não informada',
  noDelivery: 'sem delivery',
  empty:
    'Nenhum job, delivery ou tracking assíncrono foi registrado para esta visita ainda.',
});

const badge = computed(() => runtimeBadgeForVisit(props.visit));
const matrix = computed(() => runtimeMatrixForVisit(props.visit));
const events = computed(() =>
  runtimeEventsForVisit(props.visit).slice(0, props.maxEvents)
);
const analyticsEvents = computed(() =>
  runtimeAnalyticsEventsForVisit(props.visit).slice(0, props.maxEvents)
);
const hasRuntimeEvidence = computed(
  () =>
    matrix.value.length || events.value.length || analyticsEvents.value.length
);
const lastEventLabel = computed(() =>
  badge.value.lastEventAt
    ? `Último evento registrado em ${badge.value.lastEventAt}.`
    : ''
);
const eventDigest = event => runtimeEventDigest(event);
const entryToneClass = entry => runtimeToneClass(entry.tone || 'neutral');
const runtimeInfoLine = entry =>
  [
    entry.queue ? `Fila ${entry.queue}` : '',
    entry.attemptCount ? `${entry.attemptCount} tentativa(s)` : '',
    entry.deliveryStatus ? `Delivery ${entry.deliveryStatus}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
const eventDigestLine = event => {
  const digest = eventDigest(event);
  return `Fila ${digest.queue || TEXT.noQueue} · ${
    digest.attemptCount
  } tentativa(s) · ${digest.deliveryStatus || TEXT.noDelivery}`;
};
const payloadFingerprintLabel = event => {
  const fingerprint = eventDigest(event).payloadSha256;
  return fingerprint ? `Payload SHA-256 ${fingerprint}` : '';
};
</script>

<template>
  <LaisCard :title="TEXT.title" prefix-icon="i-lucide-activity">
    <div
      class="inline-flex max-w-full items-center gap-2 rounded-full border px-2.5 py-1 text-[12px] font-semibold"
      :class="runtimeToneClass(badge.tone)"
      :title="badge.title"
    >
      <span class="size-4 shrink-0" :class="badge.icon" aria-hidden="true" />
      <span class="truncate">{{ badge.label }}</span>
    </div>

    <p
      v-if="lastEventLabel"
      class="mt-2 text-[12px] leading-relaxed text-lais-text-muted"
    >
      {{ lastEventLabel }}
    </p>

    <div
      v-if="hasRuntimeEvidence"
      class="mt-4 grid gap-3"
      data-test-id="visit-operational-runtime-panel"
    >
      <div class="grid gap-2">
        <div
          v-for="entry in matrix"
          :key="entry.key"
          class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
        >
          <div class="flex min-w-0 items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="truncate text-[13px] font-semibold text-lais-text">
                {{ entry.label }}
              </p>
              <p class="mt-0.5 truncate text-[11px] text-lais-text-muted">
                {{ entry.job || TEXT.unknownJob }}
              </p>
            </div>
            <span
              class="shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
              :class="entryToneClass(entry)"
            >
              {{ runtimeStatusLabel(entry) }}
            </span>
          </div>
          <p
            v-if="runtimeInfoLine(entry)"
            class="mt-2 text-[11px] text-lais-text-muted"
          >
            {{ runtimeInfoLine(entry) }}
          </p>
        </div>
      </div>

      <details v-if="events.length" class="rounded-lais-sm bg-lais-bg-soft p-3">
        <summary
          class="cursor-pointer text-[12px] font-semibold text-lais-text"
        >
          {{ TEXT.eventsTitle }}
        </summary>
        <div class="mt-3 grid gap-2">
          <div
            v-for="event in events"
            :key="event.eventKey || event.event_key"
            class="grid gap-1 border-t border-lais-border-soft pt-2 first:border-t-0 first:pt-0"
          >
            <p class="text-[12px] font-semibold text-lais-text">
              {{ event.event || event.event_key }}
            </p>
            <p class="text-[11px] text-lais-text-muted">
              {{ eventDigestLine(event) }}
            </p>
            <p
              v-if="payloadFingerprintLabel(event)"
              class="truncate text-[11px] text-lais-text-muted"
              :title="eventDigest(event).payloadSha256"
            >
              {{ payloadFingerprintLabel(event) }}
            </p>
          </div>
        </div>
      </details>

      <details
        v-if="analyticsEvents.length"
        class="rounded-lais-sm bg-lais-bg-soft p-3"
      >
        <summary
          class="cursor-pointer text-[12px] font-semibold text-lais-text"
        >
          {{ TEXT.analyticsTitle }}
        </summary>
        <div class="mt-3 grid gap-2">
          <div
            v-for="event in analyticsEvents"
            :key="`${event.name}-${event.occurredAt || event.occurred_at}`"
            class="flex min-w-0 justify-between gap-2 border-t border-lais-border-soft pt-2 first:border-t-0 first:pt-0"
          >
            <span class="truncate text-[12px] text-lais-text-body">
              {{ event.name }}
            </span>
            <span class="shrink-0 text-[11px] text-lais-text-muted">
              {{ event.occurredAt || event.occurred_at }}
            </span>
          </div>
        </div>
      </details>
    </div>

    <p v-else class="mt-3 text-[13px] leading-relaxed text-lais-text-muted">
      {{ TEXT.empty }}
    </p>
  </LaisCard>
</template>
